import { useQuery } from '@tanstack/react-query';
import { useApi } from '../api/service';
import type {
  AvailableResponse,
  CollateralResponse,
  MinimumResponse,
  RateResponse,
  RecipientResponse,
} from './types';
import { errorHandler } from '@/lib/utils/error-handler';
import type { TranslogChain } from '@/lib/types/translog-chain.type';

export const useAvailable = () => {
  const { api } = useApi();
  const available = useQuery<AvailableResponse, Error>({
    queryKey: ['/sale/available'],
    queryFn: async () => {
      try {
        const response = await api.get<AvailableResponse>('/sale/available');
        return response.data;
      } catch (error: any) {
        errorHandler(error);
      }
    },
    refetchOnWindowFocus: true,
    staleTime: 12000,
    refetchInterval: 12000,
  });
  return { available };
};

export const useRate = () => {
  const { api } = useApi();
  const rate = useQuery<RateResponse, Error>({
    queryKey: ['/sale/rate'],
    queryFn: async () => {
      try {
        const response = await api.get<RateResponse>('/sale/rate');
        return response.data;
      } catch (error: any) {
        errorHandler(error);
      }
    },
    refetchOnWindowFocus: true,
    staleTime: 30000,
    refetchInterval: 30000,
  });
  return { rate };
};

export const useRecipient = (chain: TranslogChain) => {
  const { api } = useApi();
  const recipient = useQuery<RecipientResponse, Error>({
    queryKey: ['/sale/recipient', chain],
    queryFn: async () => {
      try {
        const response = await api.get<RecipientResponse>('/sale/recipient', { params: { chain } });
        return response.data;
      } catch (error: any) {
        errorHandler(error);
      }
    },
    refetchOnWindowFocus: true,
    staleTime: 60000,
  });
  return { recipient };
};

export const useCollateral = (chain: TranslogChain) => {
  const { api } = useApi();
  const collateral = useQuery<CollateralResponse, Error>({
    queryKey: ['/sale/collateral', chain],
    queryFn: async () => {
      try {
        const response = await api.get<CollateralResponse>('/sale/collateral', {
          params: { chain },
        });
        return response.data;
      } catch (error: any) {
        errorHandler(error);
      }
    },
    refetchOnWindowFocus: true,
    staleTime: 60000,
  });
  return { collateral };
};

export const useMinimum = () => {
  const { api } = useApi();
  const minimum = useQuery<MinimumResponse, Error>({
    queryKey: ['/sale/minimum'],
    queryFn: async () => {
      try {
        const response = await api.get<MinimumResponse>('/sale/minimum');
        return response.data;
      } catch (error: any) {
        errorHandler(error);
      }
    },
    refetchOnWindowFocus: true,
    staleTime: 30000,
    refetchInterval: 30000,
  });
  return { minimum };
};
